import React from 'react';
import ColumnChart from '../components/ColumnChart';
import ReportCard from '../components/ReportCard';
import RuleList from '../components/RuleList';
import { AXIS_META } from '../copy';
import { barLength, bucketCount, fmtCount, fmtPct, niceCeil, share, shareAtLeast, tip } from '../format';
import { Figure } from '../components/Emphasis';

/**
 * Distribution of abnormality counts, with the reading of it beside the chart.
 *
 * Column heights are scaled to a rounded count axis, not to the tallest column, so the shape of the
 * distribution is comparable between companies and between periods.
 */
const MetabolicDistribution = ({ data }) => {
  const total = data.cohort.total;
  const counts = AXIS_META.map((_, index) => bucketCount(data.buckets, index));
  const axisMax = niceCeil(Math.max(...counts), { percent: false });
  const oneAbnormality = bucketCount(data.buckets, 1);
  const syndromeCount = bucketCount(data.buckets, 3);

  const columns = AXIS_META.map((meta, index) => ({
    tick: meta.tick,
    caption: meta.caption,
    value: fmtCount(counts[index]),
    length: barLength(counts[index], axisMax),
    tip: tip(
      `${meta.tick} abnormalit${meta.tick === '1' ? 'y' : 'ies'} · ${fmtCount(counts[index])} employees`,
      `${fmtPct(share(counts[index], total))} of cohort${index === 3 ? ' — meets clinical criteria' : ''}`
    ),
  }));

  return (
    <div className='mt-ds-9 grid grid-cols-1 items-start gap-ds-5 lg:grid-cols-[1.5fr_1fr]'>
      <ReportCard fileName='abnormality-distribution' className='p-[26px]'>
        <h3 className='m-0 mb-[4px] font-fig-extrabold text-[26px] font-bold leading-[1.18] text-gray-900'>
          How many abnormalities does each employee have?
        </h3>
        <p className='m-0 mb-[28px] text-xs text-gray-600'>Employees by abnormality count</p>
        <ColumnChart columns={columns} />
      </ReportCard>

      <div className='rounded-ds-lg border border-gray-300 bg-white p-[26px]'>
        <div className='mb-ds-4 text-ts-xs font-semibold uppercase tracking-[0.12em] text-ds-brand'>
          Reading the curve
        </div>
        <RuleList
          mutedLast
          items={[
            <>
              {`Fewer than ${(share(counts[0], total) / 10).toFixed(0)} in 10 employees are entirely clear across all ${data.cohort.markers} markers.`}
            </>,
            <>
              The single largest group <Figure>({fmtPct(share(oneAbnormality, total))})</Figure> has one
              abnormality — the highest-leverage group for early lifestyle coaching.
            </>,
            <>
              <Figure>{`${fmtCount(syndromeCount)} employees`}</Figure> already meet the clinical 3+ threshold for
              metabolic syndrome today, and <Figure>{fmtPct(shareAtLeast(data.buckets, 2))}</Figure> carry two or
              more.
            </>,
            'This is a conservative picture — only a subset provided BMI and blood pressure data.',
          ]}
        />
      </div>
    </div>
  );
};

export default MetabolicDistribution;
